import { DeleteOutlined, SettingOutlined } from "@ant-design/icons";
import { Button, Col, Input, Row, Select } from "antd";
import { DrawerLink } from "components/UI/DrawerLink";
import { useGeneralForm } from "components/UI/GeneralForm/hooks/useGeneralForm";
import { usePaldockTranslation } from "hooks/usePaldockTranslation";
import { FC } from "react";
import { useWatch } from "react-hook-form";
import { FormItem } from "react-hook-form-antd";
import { RequestConnectionPropertyType } from "types/enums";
import { DrawerId } from "types/models";

import { FormData } from "./formSchema";

import FormFieldData = App.Data.FormFieldData;

const { Option } = Select;

interface RequestBodyFormProps {
    name: number;
    parentName: number;
    leadFields: Array<FormFieldData | string>;
    handleRemove: () => void;
}

export const RequestBodyForm: FC<RequestBodyFormProps> = ({ name, parentName, leadFields, handleRemove }) => {
    const { t } = usePaldockTranslation("ui.connection-creator-editor.request-body");
    const bodySetting = DrawerId.MethodPropertyBodySetting;
    const generalFormUtils = useGeneralForm<FormData>();
    const { control } = generalFormUtils;

    const propertyPath = `methods.${parentName}.requestConfig.requestConnectionProperties.${name}` as const;

    const type =
        useWatch({
            control,
            name: `${propertyPath}.type` as const,
        }) ?? RequestConnectionPropertyType.Constant;

    return (
        <Row gutter={16} align="middle">
            <Col span={6}>
                <FormItem
                    control={control}
                    name={`${propertyPath}.type`}
                    label={t("fields.type.label")}
                    labelCol={{ span: 24 }}
                >
                    <Select placeholder={t("fields.type.placeholder")}>
                        {Object.values(RequestConnectionPropertyType).map((propertyValue) => (
                            <Option value={propertyValue} key={propertyValue}>
                                {propertyValue}
                            </Option>
                        ))}
                    </Select>
                </FormItem>
            </Col>

            <Col span={6}>
                <FormItem
                    control={control}
                    name={`${propertyPath}.use_as_accessor`}
                    label={t("fields.parameter.label")}
                    labelCol={{ span: 24 }}
                >
                    <Input placeholder={t("fields.parameter.placeholder")} />
                </FormItem>
            </Col>

            <Col span={6}>
                {type === RequestConnectionPropertyType.Object ? (
                    <FormItem
                        control={control}
                        name={`${propertyPath}.accessor`}
                        label={t("fields.accessor.label")}
                        labelCol={{ span: 24 }}
                    >
                        <Select placeholder={t("fields.accessor.placeholder")}>
                            {leadFields?.map((field) =>
                                isFormFieldData(field) ? (
                                    <Option key={field.id} value={field.system_name}>
                                        {field.system_name}
                                    </Option>
                                ) : (
                                    <Option key={field} value={field}>
                                        {field}
                                    </Option>
                                )
                            )}
                        </Select>
                    </FormItem>
                ) : (
                    <FormItem
                        control={control}
                        name={`${propertyPath}.value`}
                        label={t("fields.value.label")}
                        labelCol={{ span: 24 }}
                    >
                        <Input placeholder={t("fields.value.placeholder")} />
                    </FormItem>
                )}
            </Col>

            <Col style={{ textAlign: "left" }}>
                <Button danger onClick={handleRemove} icon={<DeleteOutlined />} />
            </Col>

            <Col>
                <DrawerLink
                    drawerId={bodySetting}
                    search={{
                        [`${bodySetting}-id`]: name.toString(),
                        [`${bodySetting}-parentName`]: parentName.toString(),
                        [`${bodySetting}-propertyType`]: type,
                    }}
                    action="show"
                >
                    <Button icon={<SettingOutlined />} />
                </DrawerLink>
            </Col>
        </Row>
    );
};

const isFormFieldData = (field: FormFieldData | string): field is FormFieldData => {
    return typeof field === "object" && "id" in field && "system_name" in field;
};
